import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { timeout, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { apiResources } from '../../api/api.resources';
import {
    DocumentsOverviewInterface,
    MinutesResponseInterface,
    MinutesIndicatorsResponseInterface,
    AudioUploadResponseInterface,
    PresignedUploadUrlResponseInterface,
    AudioProcessingStatusResponseInterface,
    ContractsResponseInterface,
    RecentDocumentInterface,
    TranscriptionQuotaInterface,
    TranscriptionQuotaResponseInterface
} from './documents.interface';
import { MinuteInterface, MinuteStatusEnum, MinuteTypeEnum } from '../../../modules/admin/secretary/documents/list/tabs/minutes/minutes.interface';
import { ContractInterface, ContractTypeEnum } from '../../../modules/admin/secretary/documents/list/tabs/contracts/contracts.interface';

@Injectable({
    providedIn: 'root'
})
export class DocumentsService {
    private baseApiUrl = environment.config.apiUrl + apiResources.v1 + apiResources.secretary.base;
    private documentsUrl = this.baseApiUrl + apiResources.secretary.documents.base;
    private minutesUrl = this.documentsUrl + apiResources.secretary.documents.minutes.base;
    private contractsUrl = this.documentsUrl + apiResources.secretary.documents.contracts.base;

    constructor(private _httpClient: HttpClient) {}

    // ========================================
    // OVERVIEW
    // ========================================

    getOverview(): Observable<DocumentsOverviewInterface> {
        return this._httpClient.get<DocumentsOverviewInterface>(`${this.baseApiUrl}${apiResources.secretary.documents.overview}`);
    }

    // ========================================
    // MINUTES
    // ========================================

    getMinutes(page: number = 1, filters?: any): Observable<MinutesResponseInterface> {
        let params = new HttpParams().set('page', page.toString());

        if (filters?.term) {
            params = params.set('term', filters.term);
        }
        if (filters?.type) {
            params = params.set('type', filters.type);
        }
        if (filters?.status) {
            params = params.set('status', filters.status);
        }

        return this._httpClient.get<MinutesResponseInterface>(this.minutesUrl, { params });
    }

    getMinutesIndicators(): Observable<MinutesIndicatorsResponseInterface> {
        return this._httpClient.get<MinutesIndicatorsResponseInterface>(`${this.minutesUrl}${apiResources.secretary.documents.minutes.indicators}`);
    }

    getMinuteById(id: number): Observable<{ data: MinuteInterface }> {
        return this._httpClient.get<{ data: MinuteInterface }>(`${this.minutesUrl}/${id}`);
    }

    createMinute(data: MinuteInterface, file?: File): Observable<any> {
        if (file) {
            const formData = new FormData();
            formData.append('data', JSON.stringify(data));
            formData.append('file', file);
            return this._httpClient.post(this.minutesUrl, formData);
        }

        return this._httpClient.post(this.minutesUrl, data);
    }

    updateMinute(id: number, data: MinuteInterface, file?: File): Observable<any> {
        if (file) {
            const formData = new FormData();
            formData.append('data', JSON.stringify(data));
            formData.append('file', file);
            return this._httpClient.put(`${this.minutesUrl}/${id}`, formData);
        }

        return this._httpClient.put(`${this.minutesUrl}/${id}`, data);
    }

    updateMinuteStatus(id: number, status: string): Observable<any> {
        return this._httpClient.put(`${this.minutesUrl}/${id}/status`, { status });
    }

    deleteMinute(id: number): Observable<any> {
        return this._httpClient.delete(`${this.minutesUrl}/${id}`);
    }

    uploadAudio(file: File): Observable<AudioUploadResponseInterface> {
        const formData = new FormData();
        formData.append('file', file);
        return this._httpClient.post<AudioUploadResponseInterface>(
            `${this.minutesUrl}${apiResources.secretary.documents.minutes.uploadAudio}`,
            formData,
            { reportProgress: false }
        ).pipe(
            timeout(300000)
        );
    }

    getPresignedUploadUrl(fileName: string, contentType: string = 'audio/mpeg'): Observable<PresignedUploadUrlResponseInterface> {
        const params = new HttpParams()
            .set('fileName', fileName)
            .set('contentType', contentType);
        return this._httpClient.get<PresignedUploadUrlResponseInterface>(
            `${this.minutesUrl}${apiResources.secretary.documents.minutes.presignedUploadUrl}`,
            { params }
        );
    }

    uploadFileToPresignedUrl(uploadUrl: string, file: File): Observable<ProgressEvent> {
        return new Observable((observer) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', uploadUrl, true);
            xhr.setRequestHeader('Content-Type', file.type || 'audio/mpeg');

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    observer.next(event);
                }
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    observer.complete();
                } else {
                    observer.error(new Error(`Upload failed with status ${xhr.status}`));
                }
            };

            xhr.onerror = () => observer.error(new Error('Network error during upload'));
            xhr.send(file);

            return () => xhr.abort();
        });
    }

    confirmAudioUpload(audioUrl: string, fileName: string, minuteId?: number): Observable<AudioUploadResponseInterface> {
        const payload: any = {
            audioUrl: audioUrl,
            fileName: fileName,
        };

        if (minuteId) {
            payload.minuteId = minuteId;
        }

        return this._httpClient.post<AudioUploadResponseInterface>(
            `${this.minutesUrl}${apiResources.secretary.documents.minutes.confirmUploadAudio}`,
            payload
        ).pipe(timeout(300000));
    }

    getAudioProcessingStatus(minuteId: number): Observable<AudioProcessingStatusResponseInterface> {
        return this._httpClient.get<AudioProcessingStatusResponseInterface>(`${this.minutesUrl}${apiResources.secretary.documents.minutes.audioStatus}/${minuteId}`);
    }

    retryAudioTranscription(minuteId: number): Observable<any>
    {
        return this._httpClient.post<any>(
            `${this.minutesUrl}${apiResources.secretary.documents.minutes.retryAudioTranscription}`,
            { minuteId }
        );
    }

    getTranscriptionQuota(): Observable<TranscriptionQuotaInterface>
    {
        return this._httpClient
            .get<TranscriptionQuotaResponseInterface>(
                `${this.minutesUrl}${apiResources.secretary.documents.minutes.transcriptionQuota}`
            )
            .pipe(
                // O backend retorna { quota: {...} }; exponha apenas o payload interno.
                map(response => (response?.quota ?? (response as unknown as TranscriptionQuotaInterface)) as TranscriptionQuotaInterface)
            );
    }

    exportMinuteReport(id: number): Observable<any> {
        return this._httpClient.get(`${this.minutesUrl}/${id}/export-report`);
    }

    // ========================================
    // CONTRACTS
    // ========================================

    getContracts(page: number = 1, filters?: any): Observable<ContractsResponseInterface> {
        let params = new HttpParams().set('page', page.toString());

        if (filters?.term) {
            params = params.set('term', filters.term);
        }

        return this._httpClient.get<ContractsResponseInterface>(this.contractsUrl, { params });
    }

    getContractById(id: number): Observable<{ data: ContractInterface }> {
        return this._httpClient.get<{ data: ContractInterface }>(`${this.contractsUrl}/${id}`);
    }

    createContract(data: ContractInterface, file?: File): Observable<any> {
        const formData = new FormData();
        formData.append('title', data.title);
        formData.append('type', data.type);
        formData.append('description', data.description || '');
        formData.append('startDate', data.startDate);
        formData.append('endDate', data.endDate);
        formData.append('contractedParty', data.contractedParty || '');
        if (file) {
            formData.append('file', file);
        }
        return this._httpClient.post(this.contractsUrl, formData);
    }

    updateContract(id: number, data: ContractInterface, file?: File): Observable<any> {
        const formData = new FormData();
        formData.append('title', data.title);
        formData.append('type', data.type);
        formData.append('description', data.description || '');
        formData.append('startDate', data.startDate);
        formData.append('endDate', data.endDate);
        formData.append('contractedParty', data.contractedParty || '');
        if (file) {
            formData.append('file', file);
        }
        return this._httpClient.put(`${this.contractsUrl}/${id}`, formData);
    }

    deleteContract(id: number): Observable<any> {
        return this._httpClient.delete(`${this.contractsUrl}/${id}`);
    }
}
